import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { ButtonModule } from 'primeng/button';
import { TranslocoModule } from '@jsverse/transloco';

export interface BreadcrumbItem {
  label: string;
  route?: string;
  translationKey?: string;
}

export interface PageHeaderAction {
  label: string;
  icon?: string;
  variant: 'primary' | 'outlined';
  onClick?: () => void;
  routerLink?: string;
  badge?: { text: string; variant: 'danger' | 'normal' };
  testId?: string;
  hide?: boolean;
  loading?: boolean;
}

@Component({
  selector: 'app-page-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    LucideAngularModule,
    ButtonModule,
    TranslocoModule,
  ],
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
})
export class PageHeaderComponent implements OnInit {
  private router = inject(Router);

  @Input() title?: string;
  @Input() breadcrumbs?: BreadcrumbItem[];
  @Input() actions?: PageHeaderAction[];
  @Input() autoBreadcrumb = true;

  displayBreadcrumbs: BreadcrumbItem[] = [];

  // Mapping simple et extensible pour les routes
  private routeLabels: Record<string, string> = {
    'travel-board': 'navigation.trips',
    clients: 'navigation.clients',
    contributions: 'navigation.contributions',
    calculators: 'navigation.calculators',
    help: 'navigation.help',
    resources: 'navigation.resources',
    ressources: 'navigation.resources', // Legacy
    account: 'navigation.myAccount',
    profile: 'navigation.profile',
    dashboard: 'navigation.dashboard',
    'dashboard-temp': 'navigation.dashboard',
    'basic-calculator': 'calculatorList.basic.title',
    'holiday-calculator': 'calculatorList.holiday.title',
    'plane-calculator': 'calculatorList.plane.title',
    'file-import-calculator': 'calculatorList.file.title',
    'event-calculator': 'calculatorList.event.title',
    integration: 'calculatorList.integration.title',
    orders: 'calculatorList.orders.title',
    contact: 'calculatorList.contact.title',
    'impact-report': 'impactReport.title',
    factors: 'navigation.factors',
    objectives: 'navigation.objectives',
    updates: 'navigation.updates',
  };

  ngOnInit() {
    this.setupBreadcrumbs();
  }

  private setupBreadcrumbs() {
    if (this.breadcrumbs && this.breadcrumbs.length > 0) {
      // Utiliser les breadcrumbs fournis manuellement
      this.displayBreadcrumbs = this.breadcrumbs;
    } else if (this.autoBreadcrumb) {
      // Génération automatique
      this.displayBreadcrumbs = this.generateBreadcrumbs();
    }
  }

  private generateBreadcrumbs(): BreadcrumbItem[] {
    const segments = this.router.url
      .split('?')[0] // Remove query params
      .split('/')
      .filter((s) => s && s !== 'home');
    const breadcrumbs: BreadcrumbItem[] = [];

    let currentPath = '/home';

    segments.forEach((segment) => {
      currentPath += `/${segment}`;

      const translationKey = this.routeLabels[segment];
      breadcrumbs.push({
        label: translationKey || this.formatSegmentLabel(segment),
        route: currentPath,
        translationKey: translationKey,
      });
    });

    return breadcrumbs;
  }

  private formatSegmentLabel(segment: string): string {
    return segment
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getBadgeClass(variant: 'danger' | 'normal'): string {
    const baseClasses =
      'inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold';

    if (variant === 'danger') {
      return `${baseClasses} bg-red-500 text-white`;
    } else {
      return `${baseClasses} bg-white text-slate-800 border border-slate-200`;
    }
  }
}
